
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Folder } from "lucide-react";
import type { Template } from "@/types/template";
import { TemplateTable } from "@/components/template/TemplateTable";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

export default function CompanyLibrary() {
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user has admin role
    const checkAdminRole = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data: roles, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();

        if (error) {
          console.error('Error fetching user role:', error);
          return;
        }

        setIsAdmin(roles?.role === 'admin');
      } catch (error) {
        console.error('Error checking admin role:', error);
      }
    };

    checkAdminRole();
  }, []);

  // Fetch company templates
  const { data: templates, isLoading: templatesLoading, error: templatesError, refetch: refetchTemplates } = useQuery({
    queryKey: ['company-library-templates', isAdmin],
    queryFn: async () => {
      try {
        console.log('Fetching company library templates...');
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.log('No active session found');
          throw new Error('User not authenticated');
        }

        let query = supabase
          .from('templates')
          .select('*')
          .eq('is_company_library', true);
          
        // If not admin, only show approved templates
        if (!isAdmin) {
          query = query.eq('is_approved', true);
        }
          
        query = query.order('created_at', { ascending: false });

        const { data: templates, error: templatesError } = await query;

        if (templatesError) {
          console.error('Error fetching company library templates:', templatesError);
          throw templatesError;
        }

        console.log('Company library templates fetched:', templates);
        return templates as Template[];
      } catch (error) {
        console.error('Template fetch error:', error);
        throw error;
      }
    },
  });

  const handleDownloadTemplate = async (template: Template) => {
    try {
      console.log('Downloading template:', template);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.storage
        .from('templates')
        .download(`${template.user_id}/${template.file_path}`);

      if (error) {
        console.error('Download error:', error);
        throw error;
      }

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = template.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Template downloaded successfully",
      });
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({
        title: "Error",
        description: "Failed to download template",
        variant: "destructive",
      });
    }
  };

  const handleRefresh = async () => {
    console.log('Manually refreshing templates');
    await refetchTemplates();
    toast({
      title: "Refreshed",
      description: "Template list has been updated",
    });
  };

  const isLoading = templatesLoading;
  const error = templatesError;

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-6 text-red-500">
        Error loading content. Please try again later.
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center gap-3">
        <Folder className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Default Templates</h1>
          <p className="text-gray-500 mt-2">
            Browse templates from your organization
          </p>
        </div>
      </div>
      
      <div className="mb-4 flex justify-end">
        <Button variant="outline" onClick={handleRefresh}>
          Refresh
        </Button>
      </div>
      
      {templates && templates.length > 0 ? (
        <TemplateTable
          templates={templates}
          onDownload={handleDownloadTemplate}
          onDelete={() => {}} // Company library templates cannot be deleted by regular users
          isAdmin={isAdmin}
        />
      ) : (
        <div className="text-center py-8 text-gray-500">
          No default templates available.
        </div>
      )}
    </div>
  );
}
